import { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './Classroom.css';
import { useTranslation } from 'react-i18next';
import { 
  Button, 
  Card, 
  Typography, 
  Row, 
  Col, 
  message, 
  Spin, 
  Tag, 
  Space, 
  Empty,
  Table,
  Tooltip
} from 'antd';
import { 
  ClockCircleOutlined, 
  UserOutlined, 
  EnvironmentOutlined,
  LeftOutlined,
  RightOutlined,
  HomeOutlined,
  BookOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text, Title } = Typography;

function Classroom() {
  const { t } = useTranslation();
  const [classrooms, setClassrooms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        const startDate = moment(selectedDate)
          .startOf('week')
          .format('YYYY-MM-DD');
        const endDate = moment(selectedDate)
          .endOf('week')
          .format('YYYY-MM-DD');

        const [classroomResponse, classResponse] = await Promise.all([
          axios.get(`${API_URL}/classrooms`, {
            headers: { Authorization: `Bearer ${token}` }
          }),

          axios.get(`${API_URL}/classes/class-by-day`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { startDate, endDate }
          })
        ]);

        setClassrooms(classroomResponse.data);
        setClasses(classResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        message.error(t('classroom.fetch_error'));
        setError(t('classroom.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, t, selectedDate]);

  const formatDate = (date, format = 'short') => {
    if (format === 'short') {
      return moment(date).format('DD MMM');
    }
    return moment(date).format('dddd, DD MMMM YYYY');
  };

  const getWeekDates = (centerDate) => {
    const startOfWeek = moment(centerDate).startOf('week');
    const weekDates = [];
    
    for (let i = 0; i < 7; i++) {
      weekDates.push(moment(startOfWeek).add(i, 'days').toDate());
    }
    
    return weekDates;
  };

  const getClassesForDay = (date) => {
    const targetDate = moment(date).format('YYYY-MM-DD');
    return classes.filter(cls => {
      const classDate = moment(cls.date).format('YYYY-MM-DD');
      return classDate === targetDate;
    });
  };

  // Generate time slots (e.g., 08:00 - 18:00)
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 8;
    const endHour = 18;
    
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    
    return slots;
  };

  // Get classes for a specific time slot and date
  const getClassesForTimeSlot = (date, timeSlot) => {
    const dayClasses = getClassesForDay(date);
    
    return dayClasses.filter(cls => {
      const classStartTime = moment(cls.schedule_time, 'HH:mm').format('HH:mm');
      const slotTime = moment(timeSlot, 'HH:mm');
      const classTime = moment(classStartTime, 'HH:mm');
      const classEndTime = cls.end_time ? moment(cls.end_time, 'HH:mm') : classTime.clone().add(1, 'hour');
      
      // Check if the time slot falls within the class period
      return classTime.isSameOrBefore(slotTime) && classEndTime.isAfter(slotTime);
    });
  };

  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const numA = a.number.match(/\d+/g);
    const numB = b.number.match(/\d+/g);
    return numA && numB ? parseInt(numA[0], 10) - parseInt(numB[0], 10) : a.number.localeCompare(b.number);
  });

  const weekDates = getWeekDates(selectedDate);
  const today = moment().format('YYYY-MM-DD');
  const timeSlots = generateTimeSlots();

  const handlePreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };
  
  const handleNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const handleThisWeek = () => {
    setSelectedDate(new Date());
  };

  // Render class cell content
  const renderClassCell = (classItems) => {
    if (!classItems || classItems.length === 0) {
      return (
        <div style={{
          padding: '8px',
          textAlign: 'center',
          color: '#d9d9d9',
          fontSize: '11px',
          minHeight: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          -
        </div>
      );
    }

    return (
      <div style={{ padding: '4px' }}>
        {classItems.map((classItem, index) => (
          <Tooltip
            key={index}
            title={
              <div>
                <div><strong>{classItem.subject_name}</strong></div>
                <div>Room: {classItem.classroom_number}</div>
                <div>Teacher: {classItem.nickname}</div>
                <div>Time: {moment(classItem.schedule_time, 'HH:mm').format('HH:mm')} - {classItem.end_time ? moment(classItem.end_time, 'HH:mm').format('HH:mm') : 'N/A'}</div>
                {classItem.class_code && <div>Code: {classItem.class_code}</div>}
              </div>
            }
          >
            <div
              style={{
                padding: '6px 8px',
                marginBottom: index < classItems.length - 1 ? '4px' : '0',
                backgroundColor: '#e6f7ff',
                borderRadius: '4px',
                border: '1px solid #91d5ff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#bae7ff';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#e6f7ff';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div style={{ marginBottom: '2px' }}>
                <Text strong style={{ fontSize: '12px', color: '#1890ff', display: 'block' }}>
                  {classItem.subject_name}
                </Text>
              </div>
              <Space size={4} wrap>
                <Tag icon={<EnvironmentOutlined />} color="orange" style={{ fontSize: '10px', margin: 0 }}>
                  {classItem.classroom_number}
                </Tag>
                {classItem.nickname && (
                  <Tag icon={<UserOutlined />} color="green" style={{ fontSize: '10px', margin: 0 }}>
                    {classItem.nickname}
                  </Tag>
                )}
              </Space>
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  // Create table columns for each day
  const columns = [
    {
      title: <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Time</div>,
      dataIndex: 'time',
      key: 'time',
      width: isMobile ? 60 : 100,
      fixed: isMobile ? 'left' : false,
      render: (time) => (
        <div style={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          fontSize: isMobile ? '11px' : '13px',
          color: '#595959',
          padding: '4px'
        }}>
          <ClockCircleOutlined style={{ marginRight: '4px' }} />
          {time}
        </div>
      )
    },
    ...weekDates.map((date, index) => {
      const isToday = moment(date).format('YYYY-MM-DD') === today;
      const dayName = moment(date).format('ddd');
      const fullDate = moment(date).format('DD MMM');
      
      return {
        title: (
          <div style={{ 
            textAlign: 'center',
            backgroundColor: isToday ? '#e6f7ff' : 'transparent',
            padding: '8px 4px',
            borderRadius: '4px'
          }}>
            <div style={{ 
              fontWeight: 'bold',
              fontSize: isMobile ? '12px' : '14px',
              color: isToday ? '#1890ff' : '#000'
            }}>
              {isMobile ? dayName : moment(date).format('dddd')}
            </div>
            <div style={{ 
              fontSize: isMobile ? '10px' : '12px',
              color: isToday ? '#1890ff' : '#8c8c8c',
              marginTop: '2px'
            }}>
              {fullDate}
            </div>
          </div>
        ),
        dataIndex: `day${index}`,
        key: `day${index}`,
        width: isMobile ? 120 : 'auto',
        render: (classes) => renderClassCell(classes)
      };
    })
  ];

  // Create table data
  const tableData = timeSlots.map((timeSlot, slotIndex) => {
    const row = {
      key: slotIndex,
      time: timeSlot
    };

    weekDates.forEach((date, dayIndex) => {
      const classesInSlot = getClassesForTimeSlot(date, timeSlot);
      row[`day${dayIndex}`] = classesInSlot;
    });

    return row;
  });

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Spin size="large" tip={t('loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : '70px' }}>
        <Header />
        <div style={{ 
          padding: isMobile ? '12px' : '24px', 
          marginTop: '60px',
          backgroundColor: '#f5f5f5',
          minHeight: 'calc(100vh - 60px)'
        }}>
          {/* Header Section */}
          <Card style={{ marginBottom: '16px' }}>
            <Row align="middle" justify="space-between" gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={14}>
                <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: '#1890ff' }}>
                  <BookOutlined /> {t('Classroom Schedule')}
                </Title>
                <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                  {isMobile 
                    ? `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`
                    : `${formatDate(weekDates[0], 'long')} - ${formatDate(weekDates[6], 'long')}`
                  }
                </Text>
              </Col>
              <Col xs={24} sm={24} md={12} lg={10} style={{ 
                textAlign: isMobile ? 'center' : 'right'
              }}>
                <Space 
                  size={isMobile ? 8 : 12}
                  wrap
                >
                  <Button
                    icon={<LeftOutlined />}
                    onClick={handlePreviousWeek}
                    size={isMobile ? 'small' : 'large'}
                  >
                    {isMobile ? 'Prev' : (t('navigation.previous_week') || 'Previous')}
                  </Button>
                  <Button
                    icon={<HomeOutlined />}
                    onClick={handleThisWeek}
                    type="primary"
                    size={isMobile ? 'small' : 'large'}
                  >
                    {isMobile ? 'Today' : (t('navigation.this_week') || 'This Week')}
                  </Button>
                  <Button
                    icon={<RightOutlined />}
                    onClick={handleNextWeek}
                    size={isMobile ? 'small' : 'large'}
                  >
                    {isMobile ? 'Next' : (t('navigation.next_week') || 'Next')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Timetable */}
          <Card>
            <Table
              columns={columns as any}
              dataSource={tableData}
              pagination={false}
              bordered
              size={isMobile ? 'small' : 'middle'}
              scroll={{ 
                x: isMobile ? 800 : 'max-content',
                y: 'calc(100vh - 300px)'
              }}
              style={{
                backgroundColor: '#fff'
              }}
              rowClassName={(record, index) => {
                const currentHour = moment().format('HH:00');
                return record.time === currentHour ? 'current-time-row' : '';
              }}
            />
          </Card>

          {/* Legend */}
          <Card style={{ marginTop: '16px' }}>
            <Space size={isMobile ? 8 : 16} wrap>
              <Text strong>Legend:</Text>
              <Tag icon={<EnvironmentOutlined />} color="orange">Room</Tag>
              <Tag icon={<UserOutlined />} color="green">Teacher</Tag>
              <Tag icon={<BookOutlined />} color="blue">Subject</Tag>
            </Space>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default withRoleAccess(Classroom, ['admin', 'superadmin']);